import { styled, Box } from "@mui/material";
import { ChartOptions } from "chart.js";
import "chart.js/auto";
import _ from "lodash";
import { DateTime } from "luxon";
import { ReactElement } from "react";
import { Bar } from "react-chartjs-2";

import { palette } from "~frontend/theme";

import NumberStat from "../stats/NumberStat";

const NumberStatRow = styled(Box)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around",
  flexWrap: "wrap",
});

const ChartWrapper = styled(Box)({
  marginTop: 40,
  height: 400,
  position: "relative",
});

export default function UsageTimeline({
  startDate,
  endDate,
  data,
  donationTotals,
  showFundraisingFields,
}: {
  startDate: DateTime;
  endDate: DateTime;
  data: {
    date: string;
    smsSegments: number;
    smsMessages: number;
    mmsMessages: number;
    smsShortcodeMessages: number;
    smsShortcodeSegments: number;
    mmsShortcodeMessages: number;
    smsUnregisteredSegments: number;
    smsUnregisteredMessages: number;
    mmsUnregisteredMessages: number;
  }[];
  donationTotals: {
    donationCount: number;
    recurringDonationCount: number;
    amountRaisedInCents: number;
  };
  showFundraisingFields: boolean;
}): ReactElement {
  const labels = [];
  const smsSegmentsSeries = [];
  const mmsMessagesSeries = [];
  const smsShortcodeSegmentsSeries = [];
  const mmsShortcodeMessagesSeries = [];
  const smsUnregisteredSegmentsSeries = [];
  const mmsUnregisteredMessagesSeries = [];

  const dataByDay = _.keyBy(data, "date");

  let date = startDate;
  while (date <= endDate) {
    labels.push(
      date.toLocaleString({
        month: "short",
        day: "numeric",
      })
    );

    const {
      smsSegments,
      mmsMessages,
      smsShortcodeSegments,
      mmsShortcodeMessages,
      smsUnregisteredSegments,
      mmsUnregisteredMessages,
    } = dataByDay[date.toISODate()!] || {
      smsSegments: 0,
      mmsMessages: 0,
      smsUnregisteredSegments: 0,
      mmsUnregisteredMessages: 0,
    };
    smsSegmentsSeries.push(smsSegments);
    mmsMessagesSeries.push(mmsMessages);
    smsShortcodeSegmentsSeries.push(smsShortcodeSegments);
    mmsShortcodeMessagesSeries.push(mmsShortcodeMessages);
    smsUnregisteredSegmentsSeries.push(smsUnregisteredSegments);
    mmsUnregisteredMessagesSeries.push(mmsUnregisteredMessages);

    date = date.plus({ days: 1 });
  }

  const showUnregistered =
    _.some(smsUnregisteredSegmentsSeries, n => n > 0) ||
    _.some(mmsUnregisteredMessagesSeries, n => n > 0);

  const showShortcode =
    _.some(smsShortcodeSegmentsSeries, n => n > 0) ||
    _.some(mmsShortcodeMessagesSeries, n => n > 0);

  const chartData = {
    labels,
    datasets: [
      {
        label: "SMS Segments Sent",
        data: smsSegmentsSeries,
        backgroundColor: palette.blue,
        hoverBackgroundColor: palette.blue,
      },
      {
        label: "MMS Messages Sent",
        data: mmsMessagesSeries,
        backgroundColor: palette.navy,
        hoverBackgroundColor: palette.navy,
      },
      ...(showShortcode
        ? [
            // TODO: colors?
            {
              label: "SMS Segments Sent (Short Code)",
              data: smsShortcodeSegmentsSeries,
              backgroundColor: palette.orange,
              hoverBackgroundColor: palette.orange,
            },
            {
              label: "MMS Messages Sent (Short Code)",
              data: mmsShortcodeMessagesSeries,
              backgroundColor: palette.darkGreen,
              hoverBackgroundColor: palette.darkGreen,
            },
          ]
        : []),
      ...(showUnregistered
        ? [
            {
              label: "SMS Segments Sent (Unregistered)",
              data: smsUnregisteredSegmentsSeries,
              backgroundColor: palette.purple,
              hoverBackgroundColor: palette.purple,
            },
            {
              label: "MMS Messages Sent (Unregistered)",
              data: mmsUnregisteredMessagesSeries,
              backgroundColor: palette.midBlue,
              hoverBackgroundColor: palette.midBlue,
            },
          ]
        : []),
    ],
  };

  const chartOptions: ChartOptions<"bar"> = {
    scales: {
      y: {
        stacked: true,
        min: 0,
        ticks: {
          precision: 0,
        },
      },
      x: {
        stacked: true,
      },
    },
    plugins: {
      tooltip: {
        mode: "index",
        intersect: false,
      },
      legend: {
        onClick: () => {
          // noop -- disable default behavior of hiding series
          return;
        },
      },
    },
    maintainAspectRatio: false,
  };

  return (
    <div>
      <NumberStatRow>
        <NumberStat
          size="xlarge"
          value={_.sumBy(
            data,
            d =>
              d.smsSegments + d.smsShortcodeSegments + d.smsUnregisteredSegments
          )}
        >
          SMS Segments Sent
        </NumberStat>
        <NumberStat
          size="xlarge"
          value={_.sumBy(
            data,
            d =>
              d.smsMessages + d.smsShortcodeMessages + d.smsUnregisteredMessages
          )}
        >
          SMS Messages Sent
        </NumberStat>
        <NumberStat
          size="xlarge"
          value={_.sumBy(
            data,
            d =>
              d.mmsMessages + d.mmsShortcodeMessages + d.mmsUnregisteredMessages
          )}
        >
          MMS Messages Sent
        </NumberStat>
      </NumberStatRow>
      {showFundraisingFields ? (
        <NumberStatRow>
          <NumberStat size="xlarge" value={donationTotals.donationCount}>
            All Donations
          </NumberStat>
          <NumberStat
            size="xlarge"
            value={donationTotals.recurringDonationCount}
          >
            Recurring Donations
          </NumberStat>
          <NumberStat
            size="xlarge"
            format="usd"
            value={donationTotals.amountRaisedInCents / 100}
          >
            Total Raised
          </NumberStat>
        </NumberStatRow>
      ) : null}
      <ChartWrapper>
        <Bar data={chartData} options={chartOptions} />
      </ChartWrapper>
    </div>
  );
}
